import { useTranslation } from 'react-i18next';
import {
  TICKET_PRIORITY_ORDER,
  type TicketPriority,
  type TicketSummary,
  ticketPriorityLabelKey,
} from '@nbh/shared';
import { BreachBadge, breachBadgesFor, breachEdgeClass } from '../ui/BreachBadge';
import { PriorityBadge } from '../ui/PriorityBadge';
import { StatusBadge } from '../ui/StatusBadge';
import { type QueueChips, clearedChips, hasChips } from './queueView';

export function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`shrink-0 rounded-full px-4 py-2 text-sm font-medium ${
        // Brand-tinted outline rather than a solid fill: solid gold is already spoken
        // for — it's the "this wants a thumb" signal on the Unclaimed pill (D31) and
        // the primary-action treatment elsewhere — so an active filter borrows the
        // colour without claiming that stronger meaning.
        active
          ? 'border border-brookfields-gold bg-brookfields-gold/15 text-brookfields-gold-light shadow-[inset_0_1px_0_0_rgba(255,255,255,0.1)]'
          : 'border border-slate-700 bg-transparent text-slate-300'
      }`}
    >
      {label}
    </button>
  );
}

/**
 * The filter chips — All, Mine, Urgent, High, Medium, Low — on their own row below the
 * lifecycle tabs, never merged into them: a tab and a filter are different kinds of
 * choice, and nine controls in one strip makes both harder to read.
 *
 * Mine is a toggle and the priorities are a single choice, so Mine + Urgent is one view
 * rather than two chips fighting over the same slot. All clears both. Tapping the active
 * priority again clears it. The row scrolls sideways rather than wrapping: a wrapped row
 * pushes the first job below the fold, and that row matters most.
 */
export function QueueFilters({
  chips,
  onChange,
}: {
  chips: QueueChips;
  onChange: (next: QueueChips) => void;
}) {
  const { t } = useTranslation();

  return (
    <div
      role="group"
      aria-label={t('queue.filters')}
      className="flex gap-2 overflow-x-auto border-b border-slate-800 px-3 py-2"
    >
      <FilterChip
        label={t('queue.all')}
        active={!hasChips(chips)}
        onClick={() => onChange(clearedChips())}
      />
      <FilterChip
        label={t('queue.mine')}
        active={chips.mine}
        onClick={() => onChange({ ...chips, mine: !chips.mine })}
      />
      {TICKET_PRIORITY_ORDER.map((level) => (
        <FilterChip
          key={level}
          label={t(ticketPriorityLabelKey[level])}
          active={chips.priority === level}
          onClick={() =>
            onChange({
              ...chips,
              priority: chips.priority === level ? null : (level as TicketPriority),
            })
          }
        />
      ))}
    </div>
  );
}

export function TicketRow({
  ticket,
  now,
  onOpen,
}: {
  ticket: TicketSummary;
  /**
   * The list's shared clock — ONE `useNow` interval in `TicketQueue`, handed down,
   * never a timer per row. Fifty rows, one tick.
   */
  now: Date;
  onOpen?: (ticketId: string) => void;
}) {
  const { t } = useTranslation();
  const breaches = breachBadgesFor(ticket, now);
  const edge = breachEdgeClass(breaches);

  return (
    <button
      type="button"
      onClick={() => onOpen?.(ticket.id)}
      // The edge marker is a single-sided border, so the row drops its radius —
      // a rounded corner on a 3px edge reads as a rendering glitch, not a marker.
      className={`flex w-full flex-col items-start gap-2 bg-transparent px-4 py-3 text-left ${
        edge ? `rounded-none border-l-[3px] ${edge}` : ''
      }`}
    >
      <span className="flex w-full items-start justify-between gap-2">
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="truncate text-base font-medium text-slate-100">{ticket.title}</span>
            {breaches.map((state) => (
              <BreachBadge key={state.kind} state={state} />
            ))}
          </span>
          <span className="block truncate text-xs text-slate-500">
            #{ticket.reference} · {ticket.locationName ?? t('queue.noLocation')}
          </span>
        </span>
        <PriorityBadge priority={ticket.priority} compact />
      </span>
      <span className="flex w-full flex-wrap items-center gap-2">
        <StatusBadge status={ticket.status} holdReason={ticket.holdReason} />
        <ClaimBadge assigneeName={ticket.assigneeName} />
      </span>
    </button>
  );
}

/**
 * Who has this job, in the shared list (decision D31).
 *
 * The queue is what a whole department looks at, so it is the one place where "is
 * anyone on this" has to be answerable without opening anything. The detail screen
 * already named the assignee; the list did not, which is precisely backwards — by the
 * time you have opened a job to find out, you have already spent the tap that
 * claiming it would have cost.
 */
function ClaimBadge({ assigneeName }: { assigneeName: string | null }) {
  const { t } = useTranslation();

  if (assigneeName === null) {
    // Solid gold on dark ink (8.2:1), not a tinted chip. Unclaimed is the only row state
    // that is an offer — it wants a thumb — so it has to out-shout the settled ones
    // rather than sit beside them at equal weight. Deliberately not red: unclaimed is
    // the normal state of new work, not a fault.
    return (
      <span className="rounded-full bg-brookfields-gold px-2 py-0.5 text-xs font-semibold text-slate-950">
        {t('queue.unclaimed')}
      </span>
    );
  }

  // Quiet by design: once a job has a name on it, this line is reference, not a prompt.
  return (
    <span className="truncate rounded-full bg-slate-800 px-2 py-0.5 text-xs text-slate-300">
      {t('queue.claimedBy', { name: assigneeName })}
    </span>
  );
}
